import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import AnimeCard from "@/components/feature/AnimeCard";
import { useFavorites } from "@/hooks/useFavorites";
import { animeList } from "@/mocks/anime";

export default function Detail() {
  const { id } = useParams();
  const anime = animeList.find((item) => item.id === id);
  const { toggleFavorite, isFavorite } = useFavorites();

  const [currentEpisode, setCurrentEpisode] = useState(1);
  const [showNotice, setShowNotice] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (anime) {
      setCurrentEpisode(anime.latestEpisode);
    }
    window.scrollTo({ top: 0 });
  }, [anime]);

  const related = useMemo(() => {
    if (!anime) return [];
    return animeList
      .filter((item) => item.id !== anime.id && item.genres.some((genre) => anime.genres.includes(genre)))
      .slice(0, 4);
  }, [anime]);

  if (!anime) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary-100 text-4xl text-primary-500">
            <i className="ri-emotion-sad-line"></i>
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-950">没有找到这部动漫</h1>
          <p className="mt-2 text-sm text-foreground-500">作品可能已下架或链接有误，返回首页继续追番吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const favorited = isFavorite(anime.id);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const infoRows = [
    { label: "导演", value: anime.director, icon: "ri-user-star-line" },
    { label: "类型", value: anime.genres.join(" / "), icon: "ri-price-tag-3-line" },
    { label: "地区", value: anime.region, icon: "ri-earth-line" },
    { label: "年份", value: `${anime.year} 年`, icon: "ri-calendar-line" },
    { label: "状态", value: anime.status, icon: "ri-loader-4-line" },
    { label: "更新", value: `${anime.updatedAt} 更新至第 ${anime.latestEpisode} 集`, icon: "ri-refresh-line" },
  ];

  return (
    <Layout>
      <Seo
        title={`${anime.title}全集在线观看 - 樱花动漫`}
        description={`${anime.title}全集在线观看，${anime.genres.join("、")}题材${anime.region}动画，共${anime.totalEpisodes}集，目前更新至第${anime.latestEpisode}集。${anime.description}`}
        keywords={`${anime.title},${anime.title}在线观看,${anime.title}全集,樱花动漫,${anime.genres.join(",")}`}
        path={`/detail/${anime.id}`}
      />

      <div className="w-full px-4 pt-8 md:px-6 lg:px-10">
        <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/category/all" className="hover:text-primary-600">动漫分类</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${encodeURIComponent(anime.genres[0])}`} className="hover:text-primary-600">
            {anime.genres[0]}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{anime.title}</span>
        </nav>

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* 左侧主视觉 */}
          <div className="lg:col-span-2">
            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
              <img
                src={anime.banner || anime.cover}
                alt={`${anime.title} 主视觉`}
                title={`${anime.title} 在线观看 - 樱花动漫`}
                className="h-full w-full object-cover object-top opacity-60"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-foreground-950/50 text-center">
                <button
                  type="button"
                  onClick={() => setShowNotice(true)}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-3xl text-background-50 transition-transform hover:scale-105 cursor-pointer"
                  aria-label="播放"
                >
                  <i className="ri-play-fill"></i>
                </button>
                <p className="mt-4 text-sm text-background-100">
                  正在播放：第 {currentEpisode} 集
                </p>
                <p className="mt-1 text-xs text-accent-300">
                  演示状态，播放功能待接入
                </p>
              </div>
            </div>

            {showNotice ? (
              <div className="mt-3 flex items-center gap-2 rounded-md bg-accent-50 px-4 py-3 text-xs text-accent-800">
                <i className="ri-information-line text-base"></i>
                当前站点为内容演示环境，视频播放接口尚未接入，集数切换仅用于展示交互效果。
                <button
                  type="button"
                  onClick={() => setShowNotice(false)}
                  className="ml-auto text-accent-700 cursor-pointer"
                  aria-label="关闭提示"
                >
                  <i className="ri-close-line text-base"></i>
                </button>
              </div>
            ) : null}

            {/* 集数目录 */}
            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
                  <i className="ri-list-check-2 text-primary-500"></i>
                  播放列表
                </h2>
                <span className="text-xs text-foreground-500">
                  更新至 {anime.latestEpisode} / {anime.totalEpisodes} 集
                </span>
              </div>

              <div className="no-scrollbar mt-4 flex gap-2 overflow-x-auto pb-2 sm:grid sm:grid-cols-8 sm:overflow-visible md:grid-cols-10 lg:grid-cols-12">
                {Array.from({ length: anime.totalEpisodes }, (_, index) => index + 1).map((ep) => (
                  <button
                    key={ep}
                    type="button"
                    onClick={() => {
                      setCurrentEpisode(ep);
                      setShowNotice(true);
                    }}
                    className={`flex h-9 w-14 shrink-0 items-center justify-center rounded-md text-sm transition-colors cursor-pointer sm:w-full ${
                      ep === currentEpisode
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-50 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
                    }`}
                  >
                    {ep}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 右侧信息 */}
          <aside className="lg:col-span-1">
            <div className="flex gap-4">
              <div className="h-40 w-28 shrink-0 overflow-hidden rounded-lg bg-background-200">
                <img
                  src={anime.cover}
                  alt={`${anime.title} 封面`}
                  title={`${anime.title} 在线观看 - 樱花动漫`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold leading-snug text-foreground-950">{anime.title}</h1>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-2xl font-bold text-accent-600">{anime.rating.toFixed(1)}</span>
                  <div className="text-accent-500">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={star <= Math.round(anime.rating / 2) ? "ri-star-fill" : "ri-star-line"}
                      ></i>
                    ))}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs text-primary-700">{anime.status}</span>
                  {anime.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category/${encodeURIComponent(genre)}`}
                      className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900 hover:bg-secondary-200"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => toggleFavorite(anime.id)}
                className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer ${
                  favorited
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                {favorited ? "已收藏" : "收藏追番"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                <i className={copied ? "ri-check-line" : "ri-share-forward-line"}></i>
                {copied ? "已复制链接" : "分享"}
              </button>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-4">
              <ul className="space-y-2.5 text-sm">
                {infoRows.map((row) => (
                  <li key={row.label} className="flex gap-3">
                    <span className="flex w-20 shrink-0 items-center gap-1.5 text-foreground-500">
                      <i className={`${row.icon} text-base`}></i>
                      {row.label}
                    </span>
                    <span className="min-w-0 flex-1 text-foreground-800">{row.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* 简介 */}
        <section className="mt-10">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
            <i className="ri-file-text-line text-primary-500"></i>
            剧情简介
          </h2>
          <p className="mt-3 rounded-lg border border-background-200 bg-background-100 p-5 text-sm leading-relaxed text-foreground-700">
            {anime.description}
          </p>
        </section>

        {/* 相关推荐 */}
        {related.length > 0 ? (
          <section className="mt-10">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-sparkling-2-line text-accent-500"></i>
              相关推荐
            </h2>
            <p className="mt-1.5 text-sm text-foreground-500">根据「{anime.genres.join("、")}」题材为你推荐</p>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {related.map((item) => (
                <AnimeCard key={item.id} anime={item} />
              ))}
            </div>
          </section>
        ) : null}

        <section className="mt-10 rounded-lg bg-secondary-50 p-5 text-xs leading-relaxed text-foreground-500">
          <p>版权声明：本页面展示的内容为演示数据，相关作品版权归原著作权人所有。</p>
          <p className="mt-1">免责声明：本站不提供视频存储与播放服务，集数切换为界面演示，实际播放功能待后续接入。</p>
        </section>
      </div>
    </Layout>
  );
}